import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { 
  clampZoom,
  loadZoomFromStorage, 
  saveZoomToStorage, 
  ZOOM_CONFIG,
  ZoomContext,
  ZoomContextType,
} from '../../context/ZoomContext';

// Provider component
interface ZoomProviderProps {
  children: ReactNode;
}

export const ZoomProvider: React.FC<ZoomProviderProps> = ({ children }) => {
  const [zoomLevel, setZoomLevel] = useState<number>(loadZoomFromStorage);
  
  // Apply zoom whenever zoom level changes
  useEffect(() => {
    const root = document.querySelector('#root') as HTMLElement;
    if (root) {
      root.style.zoom = zoomLevel.toString();
    }
  }, [zoomLevel]);

  // Watch for DOM changes to reapply zoom on navigation
  useEffect(() => {
    const applyZoomToCurrentMain = () => {
      const root = document.querySelector('#root') as HTMLElement;
      if (root) {
        root.style.zoom = zoomLevel.toString();
      }
    };

    // Watch the entire document for changes
    const observer = new MutationObserver(() => {
      applyZoomToCurrentMain();
    });

    // Watch the entire document
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [zoomLevel]);

  useEffect(() => {
    const storedZoom = loadZoomFromStorage();
    setZoomLevel(storedZoom);
  }, [setZoomLevel]);
  // Set zoom level with validation and persistence
  const setZoomLevelWithValidation = useCallback((level: number): void => {
    const clampedLevel = clampZoom(level);
    setZoomLevel(clampedLevel);
    saveZoomToStorage(clampedLevel);
  }, [setZoomLevel]);
  
  // Add keyboard + mouse wheel zoom functionality
  useEffect(() => {
    const handleWheelZoom = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault(); // Prevent browser zoom
        
        const delta = event.deltaY;
        if (delta < 0) {
          // Scroll up = zoom in
          const newLevel = Math.round((zoomLevel + ZOOM_CONFIG.STEP) * 100) / 100;
          if (newLevel <= ZOOM_CONFIG.MAX) {
            setZoomLevelWithValidation(newLevel);
          }
        } else {
          // Scroll down = zoom out
          const newLevel = Math.round((zoomLevel - ZOOM_CONFIG.STEP) * 100) / 100;
          if (newLevel >= ZOOM_CONFIG.MIN) {
            setZoomLevelWithValidation(newLevel);
          }
        }
      }
    };

    window.addEventListener('wheel', handleWheelZoom, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheelZoom);
    };
  }, [zoomLevel, setZoomLevelWithValidation]);



  // Reset zoom to default
  const resetZoom = useCallback((): void => {
    setZoomLevelWithValidation(ZOOM_CONFIG.DEFAULT);
  }, [setZoomLevelWithValidation]);

  // Zoom in by one step
  const zoomIn = useCallback((): void => {
    const newLevel = Math.round((zoomLevel + ZOOM_CONFIG.STEP) * 10) / 10;
    setZoomLevelWithValidation(newLevel);
  }, [setZoomLevelWithValidation, zoomLevel]);

  // Zoom out by one step
  const zoomOut = useCallback((): void => {
    const newLevel = Math.round((zoomLevel - ZOOM_CONFIG.STEP) * 10) / 10;
    setZoomLevelWithValidation(newLevel);
  }, [zoomLevel, setZoomLevelWithValidation]);

  // Get zoom as percentage string
  const getZoomPercentage = useCallback((): string => `${Math.round(zoomLevel * 100)}%`, [zoomLevel]);

  const contextValue: ZoomContextType = useMemo(() => ({
    zoomLevel,
    setZoomLevel: setZoomLevelWithValidation,
    resetZoom,
    zoomIn,
    zoomOut,
    getZoomPercentage,
  }), [zoomLevel, setZoomLevelWithValidation, resetZoom, zoomIn, zoomOut, getZoomPercentage]);

  return <ZoomContext.Provider value={contextValue}>{children}</ZoomContext.Provider>;
};

// Export default as ZoomProvider
export default ZoomProvider;
